import { Prec, StateField, Text, type EditorState, type Extension } from "@codemirror/state";
import { Decoration, type DecorationSet, EditorView, WidgetType, lineNumbers } from "@codemirror/view";
import { foldedRanges } from "@codemirror/language";
import { preambleRange } from "./preamble-fold";

/**
 * The LaTeX source view's vertical rhythm (Olivia, 11 Oct; design-handoffs/
 * 2026-10-11-light-themes). The user: "spacing is not good, especially vertical".
 * - Blank lines between blocks are not shown (the serializer writes 0 to 2):
 *   each block gets one fixed gap instead, 8 px before a paragraph-level block,
 *   20 px (18 on a phone) before a section-level one. Blank lines inside a block
 *   keep their full height.
 * - Gaps are block widgets, so the gutter runs on and numbers stay level.
 * - Line numbers count the visible lines without gaps.
 * - The open preamble sits on a muted band.
 * - Copy (rhythmText) gives exactly one blank line between blocks.
 */

export interface RhythmOptions {
  /** 1-based first line of each block, as shown. */
  blockStarts: readonly number[];
  phone?: boolean;
}

const SECTION = /^\s*\\(part|chapter|section|subsection|subsubsection)\*?[[{]|^\s*\\begin\{document\}|^\s*\\appendix\b|^\s*\\bibliography(style)?\{|^\s*\\begin\{thebibliography\}/;

export function isSectionLine(text: string): boolean {
  return SECTION.test(text);
}

class Gap extends WidgetType {
  constructor(readonly px: number) {
    super();
  }
  eq(other: Gap) {
    return other.px === this.px;
  }
  toDOM() {
    const el = document.createElement("div");
    el.className = "cm-rhythm-gap";
    el.style.height = `${this.px}px`;
    el.setAttribute("aria-hidden", "true");
    return el;
  }
  get estimatedHeight() {
    return this.px;
  }
}

/** Where gaps go: before each block start (and \begin{document}), with the blank lines they hide. */
export function gapPlan(doc: Text, blockStarts: readonly number[]): { line: number; hidden: [number, number] | null; section: boolean }[] {
  // A block that writes nothing (a title page shown as \maketitle elsewhere)
  // "starts" on a blank line: its real start is the next line with text.
  const starts = new Set<number>();
  for (let n of blockStarts) {
    while (n <= doc.lines && n >= 1 && !doc.line(n).text.trim()) n++;
    if (n > 1 && n <= doc.lines) starts.add(n);
  }
  for (let n = 2; n <= doc.lines; n++) if (/^\s*\\begin\{document\}/.test(doc.line(n).text)) starts.add(n);
  const plan: { line: number; hidden: [number, number] | null; section: boolean }[] = [];
  for (const n of [...starts].sort((a, b) => a - b)) {
    // The blank lines just above the block's first line belong to no block.
    let first = n;
    while (first > 1 && !doc.line(first - 1).text.trim()) first--;
    if (first === 1) continue;
    plan.push({ line: n, hidden: first < n ? [first, n - 1] : null, section: isSectionLine(doc.line(n).text) });
  }
  return plan;
}

function build(state: EditorState, opts: RhythmOptions): DecorationSet {
  const doc = state.doc;
  const sectionPx = opts.phone ? 18 : 20;
  const ranges = [];
  for (const g of gapPlan(doc, opts.blockStarts)) {
    const gap = new Gap(g.section ? sectionPx : 8);
    if (g.hidden) {
      ranges.push(Decoration.replace({ block: true, widget: gap }).range(doc.line(g.hidden[0]).from, doc.line(g.hidden[1]).to));
    } else {
      ranges.push(Decoration.widget({ block: true, side: -1, widget: gap }).range(doc.line(g.line).from));
    }
  }
  // The open preamble on a muted band (folded, the bar stands in for it).
  const pre = preambleRange(state);
  if (pre) {
    let folded = false;
    foldedRanges(state).between(pre.from, pre.to, (f, t) => {
      if (f === pre.from && t === pre.to) folded = true;
    });
    if (!folded) {
      for (let n = 1; n <= pre.lines; n++) ranges.push(Decoration.line({ class: "cm-preamble-band" }).range(doc.line(n).from));
    }
  }
  return Decoration.set(ranges, true);
}

/** A line's number as the viewer shows it (blank lines between blocks are not counted), for labels
 * that name lines outside the viewer (the phone's block cards, 18 Oct). */
export function shownLineNumbers(text: string, blockStarts: readonly number[]): (line: number) => number {
  const hidden = hiddenLines(Text.of(text.split("\n")), blockStarts);
  return (line) => line - hidden.filter((h) => h < line).length;
}

/** The hidden lines (gaps), for numbering the visible ones. */
function hiddenLines(doc: Text, blockStarts: readonly number[]): number[] {
  const out: number[] = [];
  for (const g of gapPlan(doc, blockStarts)) if (g.hidden) for (let n = g.hidden[0]; n <= g.hidden[1]; n++) out.push(n);
  return out;
}

export function latexRhythm(opts: RhythmOptions): Extension {
  const field = StateField.define<{ deco: DecorationSet; hidden: number[] }>({
    create: (state) => ({ deco: build(state, opts), hidden: hiddenLines(state.doc, opts.blockStarts) }),
    update(v, tr) {
      if (!tr.docChanged && !tr.effects.length) return v;
      return { deco: build(tr.state, opts), hidden: hiddenLines(tr.state.doc, opts.blockStarts) };
    },
    provide: (f) => EditorView.decorations.from(f, (v) => v.deco),
  });
  const numbers = lineNumbers({
    formatNumber: (n, state) => {
      const v = state.field(field, false);
      if (!v) return String(n);
      // Visible lines only: count the hidden ones before this one (sorted list).
      let lo = 0;
      let hi = v.hidden.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (v.hidden[mid] < n) lo = mid + 1;
        else hi = mid;
      }
      // A gap stands where hidden blank lines were: its gutter cell stays empty.
      if (lo < v.hidden.length && v.hidden[lo] === n) return "";
      const shown = n - lo;
      const pre = n === 1 ? preambleRange(state) : null;
      if (pre) {
        let folded = false;
        foldedRanges(state).between(pre.from, pre.to, (f, t) => {
          if (f === pre.from && t === pre.to) folded = true;
        });
        // The phone's 38 px gutter can't hold "1–308"; its bar says the count.
        if (folded) return opts.phone ? "1" : `1–${pre.lines}`;
      }
      return String(shown);
    },
  });
  // The phone's numbers had no left margin and a 3-digit number overflowed its cell, cut at the
  // screen edge (the user, 18 Oct): the cell now fits its digits (content-box, at least 3) with
  // room on both sides.
  const d = opts.phone
    ? { lh: "20px", pad: "12px 16px 24px 10px", gutter: "3ch", gutterPad: ["8px", "6px"], box: "content-box" }
    : { lh: "1.6", pad: "12px 24px 24px 16px", gutter: "42px", gutterPad: ["0", "10px"], box: undefined };
  const theme = EditorView.theme({
    "&": { "--preamble-bar-h": opts.phone ? "44px" : "32px" },
    ".cm-content": { paddingTop: "12px", paddingBottom: "24px", lineHeight: d.lh },
    ".cm-line": { padding: `0 ${d.pad.split(" ")[1]} 0 ${d.pad.split(" ")[3]}`, lineHeight: d.lh },
    ".cm-gutters": {
      background: "var(--src-pane-bg, var(--lilia-surface))",
      borderRight: "1px solid var(--lilia-border)",
      color: "var(--lilia-text-muted)",
    },
    ".cm-lineNumbers .cm-gutterElement": {
      minWidth: d.gutter,
      paddingLeft: d.gutterPad[0],
      paddingRight: d.gutterPad[1],
      ...(d.box && { boxSizing: d.box }),
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      lineHeight: d.lh,
    },
    ".cm-preamble-band": { background: "var(--lilia-bg-muted, var(--muted))" },
  });
  // Above the density theme, which sets its own line heights and padding.
  return [field, numbers, Prec.highest(theme)];
}

/**
 * The text as shown, for Copy: exactly one blank line between blocks (the
 * serializer's 0 to 2 become 1), blank lines inside blocks kept.
 */
export function rhythmText(text: string, blockStarts: readonly number[]): string {
  const lines = text.split("\n");
  const starts = new Set<number>();
  for (let n of blockStarts) {
    while (n >= 1 && n <= lines.length && !lines[n - 1].trim()) n++;
    if (n > 1 && n <= lines.length) starts.add(n);
  }
  lines.forEach((l, i) => {
    if (i > 0 && /^\s*\\begin\{document\}/.test(l)) starts.add(i + 1);
  });
  const out: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const n = i + 1;
    if (starts.has(n)) {
      while (out.length && !out[out.length - 1].trim()) out.pop();
      if (out.length) out.push("");
    }
    // Blank lines just before a block start are dropped above; keep the rest.
    if (!lines[i].trim()) {
      let j = i;
      while (j < lines.length && !lines[j].trim()) j++;
      if (j < lines.length && starts.has(j + 1)) {
        i = j - 1;
        continue;
      }
    }
    out.push(lines[i]);
  }
  return out.join("\n");
}
